import React, { useState, useEffect, useContext } from "react";
import Axios from "axios";
import Select from "react-select";
import { useParams } from "react-router-dom";
import { useShelfContext } from "../../../context/ShelfContext";
import { AuthContext } from "../../../context/AuthContext";
import "./updateProduct.css";

const API = process.env.REACT_APP_API_URL;

const UpdateProduct = () => {
  const { id } = useParams();

  const { shelves, isFetching: shelvesLoading } = useShelfContext();

  const { user } = useContext(AuthContext);

  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const [formData, setFormData] = useState({
    name: "",
    barcode: "",
    part_number: "",
    initial_sale_price: 0,
    last_sale_price: 0,
    last_purchase_price: 0,
    description: "",
    shelf_ids: [],
  });

  const [loading, setLoading] = useState(true);

  const [submitLoading, setSubmitLoading] = useState(false);

  const [message, setMessage] = useState("");

  const [error, setError] = useState("");

  const [newPurchasePrice, setNewPurchasePrice] = useState("");

  const [purchasePriceMessage, setPurchasePriceMessage] = useState({
    type: "",
    text: "",
  });

  const [showPurchasePriceConfirm, setShowPurchasePriceConfirm] =
    useState(false);

  const [purchasePriceConfirmationName, setPurchasePriceConfirmationName] =
    useState("");

  const [purchasePriceLoading, setPurchasePriceLoading] = useState(false);

  const [persistedProductName, setPersistedProductName] = useState("");

  // 🔢 حالة لوحة الكمية المنفصلة
  const [currentQuantity, setCurrentQuantity] = useState(0);

  const [newQuantity, setNewQuantity] = useState("");

  const [qtyNote, setQtyNote] = useState("");

  const [qtyLoading, setQtyLoading] = useState(false);

  const [qtyMsg, setQtyMsg] = useState({
    type: "",
    text: "",
  });

  const [showQuantityConfirm, setShowQuantityConfirm] = useState(false);

  const [quantityConfirmationName, setQuantityConfirmationName] =
    useState("");

  const [pendingRequest, setPendingRequest] = useState(null);

  // 📦 جلب بيانات المنتج
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await Axios.get(`${API}/product/getProduct.php?id=${id}`);

        if (res.data.status === "success") {
          const product = res.data.product;

          setFormData({
            name: product.name || "",
            barcode: product.barcode || "",
            part_number: product.part_number || "",

            // ✅ سعر البيع الأولي
            initial_sale_price: product.initial_sale_price ?? 0,

            last_sale_price: product.last_sale_price ?? 0,

            last_purchase_price: product.last_purchase_price ?? 0,

            description: product.description || "",

            shelf_ids: product.shelf_ids || [],
          });

          setNewPurchasePrice(String(product.last_purchase_price ?? 0));

          setPersistedProductName(product.name || "");

          setCurrentQuantity(product.quantity ?? 0);

          setNewQuantity(String(product.quantity ?? 0));
        } else {
          setError(res.data.message || "فشل في تحميل بيانات الصنف");
        }
      } catch (err) {
        setError(err.response?.data?.message || "فشل في تحميل بيانات الصنف");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // ⏳ جلب طلب الكمية المعلّق إن وُجد
  const fetchPending = async () => {
    try {
      const res = await Axios.get(
        `${API}/product/getProductPendingRequest.php?product_id=${id}`,
      );

      if (res.data.status === "success") {
        setPendingRequest(res.data.pending || null);
      }
    } catch (err) {
      // تجاهل الخطأ
    }
  };

  useEffect(() => {
    fetchPending();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previousData) => ({
      ...previousData,
      [name]: value,
    }));
  };

  const handleShelfChange = (selectedOptions) => {
    const selectedIds = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];

    setFormData((previousData) => ({
      ...previousData,
      shelf_ids: selectedIds,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");
    setMessage("");

    if (!formData.name.trim()) {
      setError("اسم الصنف مطلوب");
      return;
    }

    if (!formData.part_number.trim()) {
      setError("رقم القطعة مطلوب");
      return;
    }

    setSubmitLoading(true);

    try {
      const editableProductData = {
        name: formData.name,
        barcode: formData.barcode,
        part_number: formData.part_number,
        initial_sale_price: formData.initial_sale_price,
        last_sale_price: formData.last_sale_price,
        description: formData.description,
        shelf_ids: formData.shelf_ids,
      };

      const res = await Axios.post(
        `${API}/product/updateProduct.php?id=${id}`,
        editableProductData,
      );

      if (res.data.status === "success") {
        setMessage(res.data.message);
        setPersistedProductName(res.data.product?.name || formData.name);
      } else {
        setError(res.data.message || "فشل في تحديث الصنف");
      }
    } catch (err) {
      setError(err.response?.data?.message || "فشل الاتصال بالسيرفر");
    } finally {
      setSubmitLoading(false);
    }
  };

  // 🔢 إرسال تعديل الكمية
  // تطبيق مباشر للمدير أو طلب للمستخدم
  const submitQuantityUpdate = async (quantity, confirmationName = "") => {
    setQtyLoading(true);

    try {
      const res = await Axios.post(`${API}/product/updateQuantity.php`, {
        product_id: Number(id),
        new_quantity: quantity,
        note: qtyNote,
        confirmation_name: confirmationName,
      });

      if (res.data.status === "success") {
        if (res.data.applied) {
          setCurrentQuantity(res.data.quantity);
          setNewQuantity(String(res.data.quantity));
          setQtyMsg({
            type: "ok",
            text: res.data.message,
          });
          setQtyNote("");
          setShowQuantityConfirm(false);
          setQuantityConfirmationName("");
        } else {
          setQtyMsg({
            type: "ok",
            text: res.data.message,
          });
          setQtyNote("");
          fetchPending();
        }
      } else {
        setQtyMsg({
          type: "err",
          text: res.data.message || "فشل تعديل الكمية",
        });
      }
    } catch (err) {
      setQtyMsg({
        type: "err",
        text: err.response?.data?.message || "فشل الاتصال بالسيرفر",
      });
    } finally {
      setQtyLoading(false);
    }
  };

  const handleQuantitySubmit = () => {
    setQtyMsg({
      type: "",
      text: "",
    });

    const quantity = parseInt(newQuantity, 10);

    if (Number.isNaN(quantity) || quantity < 0) {
      setQtyMsg({
        type: "err",
        text: "أدخل كمية صحيحة",
      });

      return;
    }

    if (quantity === currentQuantity) {
      setQtyMsg({
        type: "err",
        text: "الكمية الجديدة مطابقة للحالية",
      });

      return;
    }

    if (isSuperAdmin) {
      setQuantityConfirmationName("");
      setShowQuantityConfirm(true);
      return;
    }

    submitQuantityUpdate(quantity);
  };

  const closeQuantityConfirmation = () => {
    if (qtyLoading) return;

    setShowQuantityConfirm(false);
    setQuantityConfirmationName("");
  };

  const handleConfirmedQuantitySubmit = () => {
    if (
      quantityConfirmationName.trim() !== persistedProductName.trim() ||
      qtyLoading
    ) {
      return;
    }

    submitQuantityUpdate(
      parseInt(newQuantity, 10),
      quantityConfirmationName.trim(),
    );
  };

  const normalizePriceInput = (value) => {
    const sanitized = value.replace(/[^0-9.]/g, "");
    const [integerPart, ...decimalParts] = sanitized.split(".");

    return decimalParts.length > 0
      ? `${integerPart}.${decimalParts.join("")}`
      : integerPart;
  };

  const formatPrice = (value) =>
    Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const openPurchasePriceConfirmation = () => {
    setPurchasePriceMessage({ type: "", text: "" });

    if (
      newPurchasePrice.trim() === "" ||
      !Number.isFinite(Number(newPurchasePrice)) ||
      Number(newPurchasePrice) < 0
    ) {
      setPurchasePriceMessage({
        type: "err",
        text: "أدخل سعر شراء صحيحاً",
      });
      return;
    }

    if (Number(newPurchasePrice) === Number(formData.last_purchase_price)) {
      setPurchasePriceMessage({
        type: "err",
        text: "سعر الشراء الجديد مطابق للسعر الحالي",
      });
      return;
    }

    setPurchasePriceConfirmationName("");
    setShowPurchasePriceConfirm(true);
  };

  const closePurchasePriceConfirmation = () => {
    if (purchasePriceLoading) return;

    setShowPurchasePriceConfirm(false);
    setPurchasePriceConfirmationName("");
  };

  const handlePurchasePriceSubmit = async () => {
    if (
      purchasePriceConfirmationName.trim() !== persistedProductName.trim() ||
      purchasePriceLoading
    ) {
      return;
    }

    setPurchasePriceLoading(true);
    setPurchasePriceMessage({ type: "", text: "" });

    try {
      const res = await Axios.post(
        `${API}/product/updateLastPurchasePrice.php`,
        {
          product_id: Number(id),
          new_price: Number(newPurchasePrice),
          confirmation_name: purchasePriceConfirmationName.trim(),
        },
      );

      if (res.data.status !== "success") {
        throw new Error(res.data.message || "فشل تحديث سعر الشراء الأخير");
      }

      const updatedPrice = Number(res.data.new_price);

      setFormData((previousData) => ({
        ...previousData,
        last_purchase_price: updatedPrice,
      }));
      setNewPurchasePrice(String(updatedPrice));
      setPurchasePriceMessage({
        type: "ok",
        text: res.data.message,
      });
      setShowPurchasePriceConfirm(false);
      setPurchasePriceConfirmationName("");
    } catch (err) {
      setPurchasePriceMessage({
        type: "err",
        text:
          err.response?.data?.message ||
          err.message ||
          "فشل الاتصال بالسيرفر",
      });
    } finally {
      setPurchasePriceLoading(false);
    }
  };

  const shelfOptions = shelves.map((shelf) => ({
    value: shelf.id,
    label: shelf.name,
  }));

  return (
    <div className="np-page">
      {loading ? (
        <p>جارٍ تحميل بيانات الصنف...</p>
      ) : (
        <div className="np-layout">
          {/* ===== نموذج بيانات الصنف ===== */}
          <div className="np-card">
            <div className="np-header">
              <h2 className="np-title">تحديث الصنف</h2>

              <p className="np-subtitle">
                عدّل بيانات الصنف ثم اضغط على زر التحديث
              </p>
            </div>

            <form className="np-form" onSubmit={handleSubmit}>
              <div className="np-row">
                <div className="np-form-group">
                  <label className="np-label">
                    اسم الصنف <span className="np-required">*</span>
                  </label>

                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="np-input"
                    required
                  />
                </div>

                <div className="np-form-group">
                  <label className="np-label">
                    رقم القطعة <span className="np-required">*</span>
                  </label>

                  <input
                    type="text"
                    name="part_number"
                    value={formData.part_number}
                    onChange={handleChange}
                    className="np-input"
                    required
                  />
                </div>
              </div>

              <div className="np-row">
                <div className="np-form-group">
                  <label className="np-label">
                    الباركود <span className="np-required">*</span>
                  </label>

                  <input
                    type="text"
                    name="barcode"
                    value={formData.barcode}
                    onChange={handleChange}
                    className="np-input"
                    required
                  />
                </div>
              </div>

              <div className="np-row">
                {/* ✅ سعر البيع الأولي */}
                <div className="np-form-group">
                  <label className="np-label">سعر البيع الأولي</label>

                  <input
                    type="text"
                    inputMode="decimal"
                    name="initial_sale_price"
                    value={formData.initial_sale_price}
                    onChange={(event) => {
                      const value = event.target.value.replace(/[^0-9.]/g, "");

                      handleChange({
                        target: {
                          name: "initial_sale_price",
                          value,
                        },
                      });
                    }}
                    className="np-input"
                    placeholder="0"
                  />
                </div>

                <div className="np-form-group">
                  <label className="np-label">
                    سعر البيع الأخير <span className="np-required">*</span>
                  </label>

                  <input
                    type="text"
                    inputMode="decimal"
                    name="last_sale_price"
                    value={formData.last_sale_price}
                    onChange={(event) => {
                      const value = event.target.value.replace(/[^0-9.]/g, "");

                      handleChange({
                        target: {
                          name: "last_sale_price",
                          value,
                        },
                      });
                    }}
                    className="np-input"
                    required
                  />
                </div>
              </div>

              {/* ✅ الرفوف - اختيار متعدد */}
              <div className="np-form-group">
                <label className="np-label">
                  الرفوف (يمكن اختيار أكثر من رف)
                </label>

                <Select
                  isMulti
                  options={shelfOptions}
                  value={shelfOptions.filter((option) =>
                    formData.shelf_ids.includes(option.value),
                  )}
                  onChange={handleShelfChange}
                  isClearable
                  isLoading={shelvesLoading}
                  placeholder="اختر رفاً أو أكثر..."
                  filterOption={(option, inputValue) => {
                    if (!inputValue) {
                      return true;
                    }

                    const searchWords = inputValue
                      .toLowerCase()
                      .split(" ")
                      .filter((word) => word);

                    const optionText = option.label.toLowerCase();

                    return searchWords.every((word) =>
                      optionText.includes(word),
                    );
                  }}
                />
              </div>

              <div className="np-row">
                <div
                  className="np-form-group"
                  style={{
                    flex: "1 1 100%",
                  }}
                >
                  <label className="np-label">الوصف</label>

                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    className="np-textarea"
                  />
                </div>
              </div>

              {error && <p className="np-error">{error}</p>}

              {message && <p className="np-success">{message}</p>}

              <button
                type="submit"
                className={`np-submit ${submitLoading ? "loading" : ""}`}
                disabled={submitLoading}
              >
                {submitLoading ? <div className="np-spinner" /> : "تحديث الصنف"}
              </button>
            </form>
          </div>

          <div className="np-side-column">
            {isSuperAdmin && (
              <section className="purchase-price-card">
                <h3>آخر سعر شراء</h3>

                <p className="purchase-price-sub">
                  هذا التعديل متاح للمدير العام فقط.
                </p>

                <div className="purchase-price-current">
                  <span>السعر الحالي:</span>
                  <strong dir="ltr">
                    {formatPrice(formData.last_purchase_price)} ₪
                  </strong>
                </div>

                <div className="purchase-price-field">
                  <label htmlFor="lastPurchasePrice">السعر الجديد</label>
                  <input
                    id="lastPurchasePrice"
                    type="text"
                    inputMode="decimal"
                    value={newPurchasePrice}
                    onChange={(event) =>
                      setNewPurchasePrice(
                        normalizePriceInput(event.target.value),
                      )
                    }
                    placeholder="0.00"
                  />
                </div>

                <button
                  type="button"
                  className="purchase-price-review-btn"
                  onClick={openPurchasePriceConfirmation}
                >
                  تغيير سعر الشراء
                </button>

                {purchasePriceMessage.text && (
                  <p
                    className={
                      "purchase-price-message " + purchasePriceMessage.type
                    }
                  >
                    {purchasePriceMessage.text}
                  </p>
                )}
              </section>
            )}

            {/* ===== لوحة الكمية المنفصلة ===== */}
            <div className="qty-card">
            <h3>الكمية</h3>

            <p className="qty-sub">
              {isSuperAdmin
                ? "بصفتك مديراً، يُطبَّق التعديل مباشرة."
                : "تعديل الكمية يتطلّب موافقة المدير."}
            </p>

            <div className="qty-current">
              <span className="qty-current-label">الكمية الحالية:</span>

              <span className="qty-current-value">{currentQuantity}</span>
            </div>

            {/* لافتة الطلب المعلّق */}
            {pendingRequest && (
              <div className="qty-pending">
                <div className="qty-pending-title">⏳ طلب تعديل معلّق</div>

                <div>
                  <span className="qty-change">
                    {pendingRequest.old_quantity} ←{" "}
                    {pendingRequest.new_quantity}
                  </span>
                </div>

                {pendingRequest.requested_by_name && (
                  <div
                    style={{
                      marginTop: 6,
                    }}
                  >
                    مقدّم الطلب: {pendingRequest.requested_by_name}
                  </div>
                )}

                {pendingRequest.note && (
                  <div
                    style={{
                      marginTop: 4,
                    }}
                  >
                    ملاحظة: {pendingRequest.note}
                  </div>
                )}

                {isSuperAdmin && (
                  <div
                    style={{
                      marginTop: 6,
                    }}
                  >
                    راجع الطلب من صفحة «طلبات الكمية».
                  </div>
                )}
              </div>
            )}

            <div className="qty-field">
              <label>الكمية الجديدة</label>

              <input
                type="text"
                inputMode="numeric"
                className="qty-input"
                value={newQuantity}
                onChange={(event) =>
                  setNewQuantity(event.target.value.replace(/[^0-9]/g, ""))
                }
              />
            </div>

            <div className="qty-field">
              <label>
                ملاحظة <span className="qty-note-hint">(اختياري)</span>
              </label>

              <textarea
                className="qty-textarea"
                rows="2"
                value={qtyNote}
                onChange={(event) => setQtyNote(event.target.value)}
                placeholder={
                  isSuperAdmin ? "سبب التعديل..." : "سبب طلب التعديل..."
                }
              />
            </div>

            <button
              type="button"
              className={`qty-btn ${isSuperAdmin ? "admin" : "user"}`}
              onClick={handleQuantitySubmit}
              disabled={qtyLoading}
            >
              {qtyLoading
                ? "جارٍ الإرسال..."
                : isSuperAdmin
                  ? "تحديث الكمية"
                  : "إرسال طلب للمدير"}
            </button>

            {qtyMsg.text && (
              <p className={`qty-msg ${qtyMsg.type === "ok" ? "ok" : "err"}`}>
                {qtyMsg.text}
              </p>
            )}
            </div>
          </div>

          {isSuperAdmin && showPurchasePriceConfirm && (
            <div
              className="confirmation-modal-overlay"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closePurchasePriceConfirmation();
                }
              }}
            >
              <div
                className="confirmation-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="purchasePriceConfirmTitle"
              >
                <h3 id="purchasePriceConfirmTitle">
                  تأكيد تغيير سعر الشراء
                </h3>

                <p className="confirmation-change-summary">
                  تأكيد تغيير سعر الشراء للصنف{" "}
                  <strong>{persistedProductName}</strong> من{" "}
                  <span dir="ltr">
                    {formatPrice(formData.last_purchase_price)} ₪
                  </span>{" "}
                  إلى{" "}
                  <span dir="ltr">{formatPrice(newPurchasePrice)} ₪</span>
                </p>

                <label
                  className="confirmation-name-label"
                  htmlFor="purchasePriceConfirmationName"
                >
                  اكتب اسم الصنف{" "}
                  <strong>{persistedProductName}</strong> لتفعيل زر التأكيد
                </label>

                <input
                  id="purchasePriceConfirmationName"
                  type="text"
                  className="confirmation-name-input"
                  value={purchasePriceConfirmationName}
                  onChange={(event) =>
                    setPurchasePriceConfirmationName(event.target.value)
                  }
                  autoComplete="off"
                  autoFocus
                />

                {purchasePriceMessage.type === "err" &&
                  purchasePriceMessage.text && (
                    <p className="purchase-price-message err">
                      {purchasePriceMessage.text}
                    </p>
                  )}

                <div className="confirmation-modal-actions">
                  <button
                    type="button"
                    className="confirmation-cancel-btn"
                    onClick={closePurchasePriceConfirmation}
                    disabled={purchasePriceLoading}
                  >
                    إلغاء
                  </button>
                  <button
                    type="button"
                    className="confirmation-submit-btn"
                    onClick={handlePurchasePriceSubmit}
                    disabled={
                      purchasePriceLoading ||
                      purchasePriceConfirmationName.trim() !==
                        persistedProductName.trim()
                    }
                  >
                    {purchasePriceLoading
                      ? "جارٍ التعديل..."
                      : "تأكيد تغيير السعر"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {isSuperAdmin && showQuantityConfirm && (
            <div
              className="confirmation-modal-overlay"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closeQuantityConfirmation();
                }
              }}
            >
              <div
                className="confirmation-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="quantityConfirmTitle"
              >
                <h3 id="quantityConfirmTitle">تأكيد تغيير الكمية</h3>

                <p className="confirmation-change-summary">
                  تأكيد تغيير كمية الصنف{" "}
                  <strong>{persistedProductName}</strong> من{" "}
                  <span dir="ltr">{currentQuantity}</span> إلى{" "}
                  <span dir="ltr">{newQuantity}</span>
                </p>

                <label
                  className="confirmation-name-label"
                  htmlFor="quantityConfirmationName"
                >
                  اكتب اسم الصنف{" "}
                  <strong>{persistedProductName}</strong> لتفعيل زر التأكيد
                </label>

                <input
                  id="quantityConfirmationName"
                  type="text"
                  className="confirmation-name-input"
                  value={quantityConfirmationName}
                  onChange={(event) =>
                    setQuantityConfirmationName(event.target.value)
                  }
                  autoComplete="off"
                  autoFocus
                />

                {qtyMsg.type === "err" && qtyMsg.text && (
                  <p className="qty-msg err">{qtyMsg.text}</p>
                )}

                <div className="confirmation-modal-actions">
                  <button
                    type="button"
                    className="confirmation-cancel-btn"
                    onClick={closeQuantityConfirmation}
                    disabled={qtyLoading}
                  >
                    إلغاء
                  </button>
                  <button
                    type="button"
                    className="confirmation-submit-btn"
                    onClick={handleConfirmedQuantitySubmit}
                    disabled={
                      qtyLoading ||
                      quantityConfirmationName.trim() !==
                        persistedProductName.trim()
                    }
                  >
                    {qtyLoading ? "جارٍ التعديل..." : "تأكيد تغيير الكمية"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UpdateProduct;
